import { QueryClient } from 'react-query'
import { getContext } from 'redux-saga/effects'

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false
      }
    }
  })
}

export const defaultQueryClient: QueryClient = createQueryClient()

export let queryClient: QueryClient = defaultQueryClient

export function setCustomQueryClient(newQueryClient?: QueryClient) {
  queryClient = newQueryClient ? newQueryClient : defaultQueryClient
}

export const DISABLED_QUERY = {
  enabled: false
}

/**
 * Create effect that calls `queryClient.fetchQuery`.
 *
 * Example
 *
 *    const connections: Connection[] = yield fetchQueryEffect(connectionsQuery)
 *
 * Uses queryClient provided in redux-saga context.
 */
export function* fetchQueryEffect<T>(query: T) {
  // eslint-disable-next-line redux-saga/no-unhandled-errors
  const queryClient: QueryClient = yield getContext('queryClient')

  if (!queryClient) {
    throw new Error('queryClient not defined')
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return yield queryClient.fetchQuery(query as any)
}

export function queryClientUpdateCachedValue<T = unknown>(query: readonly string[], element: T) {
  queryClient.setQueryData<T | undefined>(query, (currentValue: T | undefined) => {
    if (!currentValue) {
      return undefined
    }

    return (currentValue = { ...currentValue, ...element })
  })
}

export function queryClientUpdateExistingCachedArrayElement<T = unknown>(
  query: readonly string[],
  element: T,
  keyField: keyof T = 'id' as keyof T
) {
  queryClient.setQueryData<T[] | undefined>(query, (currentArray: T[] | undefined) => {
    if (!currentArray) {
      return undefined
    }

    currentArray = [...currentArray]
    const elementIdx = currentArray.findIndex((entry) => entry[keyField] === element[keyField])
    if (elementIdx !== -1) {
      currentArray[elementIdx] = { ...currentArray[elementIdx], ...element }
    }

    return currentArray
  })
}
export function queryClientUpdateCacheArrayElement<T = unknown>(
  query: readonly string[],
  element: T,
  keyField: keyof T = 'id' as keyof T
) {
  queryClient.setQueryData<T[] | undefined>(query, (currentArray: T[] | undefined) => {
    if (!currentArray) {
      return [element]
    }

    currentArray = [...currentArray]
    const elementIdx = currentArray.findIndex((entry) => entry[keyField] === element[keyField])
    if (elementIdx !== -1) {
      currentArray[elementIdx] = element
    } else {
      currentArray.push(element)
    }

    return currentArray
  })
}

export function queryClientRemoveCacheArrayElement<T = unknown>(
  query: readonly string[],
  id: unknown,
  keyField: keyof T = 'id' as keyof T
) {
  queryClient.setQueryData<T[] | undefined>(query, (currentArray: T[] | undefined) => {
    if (!currentArray) {
      return currentArray
    }

    const elementIdx = currentArray.findIndex((entry) => entry[keyField] === id)
    if (elementIdx !== -1) {
      currentArray = [...currentArray]
      currentArray?.splice(elementIdx, 1)
    }

    return currentArray
  })
}
